import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

/**
 * Card container: white background, shadow, rounded corners.
 * Inactive state: opacity 0.55.
 */
export const Card = styled.article<{ $inactive: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
  box-shadow: ${({ theme }) => theme.shadows.light.medium};
  overflow: hidden;
  opacity: ${({ $inactive }) => ($inactive ? 0.55 : 1)};
`;

/**
 * Header: amber background, white title, white kebab menu.
 * Desktop (>=lg): min-height 4rem, padding-left 2rem.
 * Mobile (<lg): min-height 3.2rem, padding-left 1.2rem.
 * Com `$flat`, sem cantos arredondados (usado quando a faixa de oferta vem
 * acima do cabeçalho).
 */
export const CardHeader = styled.header<{ $flat?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${({ theme }) => theme.spacing(10)};
  padding-left: ${({ theme }) => theme.spacing(5)};
  padding-right: ${({ theme }) => theme.spacing(2.5)};
  background-color: ${({ theme }) => theme.colors.amber};
  border-radius: ${({ theme, $flat }) =>
    $flat
      ? 0
      : `${theme.borderRadius.extraLarge} ${theme.borderRadius.extraLarge} 0 0`};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    min-height: ${({ theme }) => theme.spacing(8)};
    padding-left: ${({ theme }) => theme.spacing(3)};
    padding-right: ${({ theme }) => theme.spacing(1.5)};
  }
`;

/**
 * Faixa "Passagem encontrada" no topo do cartão (como no app): fundo azul,
 * texto escuro, link para o vendedor quando houver URL.
 */
export const OfferStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(5)};
  background-color: ${({ theme }) => theme.colors.vividBlue};
  color: ${({ theme }) => theme.colors.ink};
  text-decoration: none;

  a&:hover {
    filter: brightness(0.96);
  }

  a&:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ink};
    outline-offset: -4px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  }
`;

export const OfferInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const OfferTitle = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.bold};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

export const OfferDate = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
  }
`;

export const OfferPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
`;

export const OfferCity = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
  }
`;

export const OfferValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1.1;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;
/**
 * Status label: white, bold.
 * Desktop: lg (1.45rem).
 * Mobile: md (1.2rem).
 */
export const StatusLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

/**
 * Menu wrapper: relative positioning for absolute menu dropdown.
 */
export const MenuWrapper = styled.div`
  position: relative;
`;

/**
 * Kebab menu button: white icon, large clickable area.
 */
export const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing(6)};
  height: ${({ theme }) => theme.spacing(6)};
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    color: ${({ theme }) => theme.colors.white};
  }
`;

/**
 * Dropdown menu: positioned below button, white bg, shadow.
 */
export const Menu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-width: ${({ theme }) => theme.spacing(28)};
  padding: ${({ theme }) => theme.spacing(0.5)} 0;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.dividerGray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.light.medium};
`;

/**
 * Base menu item styles: block, full width, left-aligned text.
 */
const menuItemStyles = css`
  display: block;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2.5)};
  text-align: left;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightBlue};
  }
`;

/**
 * Menu item button: gray text, red for danger actions.
 */
export const MenuItemButton = styled.button<{ $danger?: boolean }>`
  ${menuItemStyles}
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.red : theme.colors.mediumGray};
`;

/**
 * Menu item link: gray text, same hover as button.
 */
export const MenuItemLink = styled(Link)`
  ${menuItemStyles}
  color: ${({ theme }) => theme.colors.mediumGray};
`;

/**
 * Body container: 2x2 grid on >=sm and <lg, 1 column below sm.
 * Desktop (>=lg): padding 2rem, column-gap 2rem, row-gap 4rem.
 * Tablet/Mobile (<lg): padding 1.2rem, column-gap 0.75rem, row-gap 1rem.
 */
export const BodyWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${({ theme }) => theme.spacing(2)};
  row-gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(5)};
  background-color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    column-gap: ${({ theme }) => theme.spacing(0.75)};
    row-gap: ${({ theme }) => theme.spacing(2.5)};
    padding: ${({ theme }) => theme.spacing(3)};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Row: flex column, holds label/value + badges.
 * min-width: 0 so grid children can shrink for text wrapping.
 */
export const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
  min-width: 0;
`;

/**
 * Row que ocupa a largura toda do grid (tipo de alerta, canais).
 */
export const FullRow = styled(Row)`
  grid-column: 1 / -1;
`;

/**
 * Label: cyan, regular.
 * Desktop: md (1.2rem).
 * Mobile: sm (0.9rem).
 */
export const RowLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.cyan};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

/**
 * Value: dark gray, bold.
 * Desktop: xl (2rem).
 * Mobile: lg (1.45rem).
 */
export const RowValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.darkGray};
  line-height: 1.2;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

/**
 * Sub-line: ink, regular.
 * Desktop: sm (0.9rem).
 * Mobile: xxs (0.8rem).
 */
export const RowSubLine = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.ink};
  line-height: 1.3;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
  }
`;

/**
 * Badges container: flex, no wrap (chips must fit).
 */
export const Badges = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

/**
 * Badge/chip: rectangular, bold ink text.
 * Desktop: sm (0.9rem) padding, radius small.
 * Mobile: xxs (0.8rem) padding, smaller padding.
 * Colors: outbound/open=yellow, return=cyan.
 */
export const Badge = styled.span<{
  $variant?: "outbound" | "return" | "open" | "channel";
}>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing(0.75)} ${({ theme }) => theme.spacing(1.5)};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.ink};
  background-color: ${({ theme, $variant }) => {
    switch ($variant) {
      case "outbound":
      case "open":
        return theme.colors.brightYellow;
      case "return":
        return theme.colors.cyan;
      case "channel":
        return theme.colors.lightGray;
      default:
        return theme.colors.brightYellow;
    }
  }};
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
    padding: ${({ theme }) => theme.spacing(0.5)} ${({ theme }) => theme.spacing(1)};
  }
`;

/**
 * Footer: divider top, flex row space-between.
 * Desktop: padding 2.5rem.
 * Mobile: padding 1.2rem.
 */
export const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(2.5)};
  border-top: 1px solid ${({ theme }) => theme.colors.dividerGray};
  background-color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => theme.spacing(1.5)};
  }
`;

/**
 * Footer label: "Por menos de", dark ink.
 * Desktop: md (1.2rem).
 * Mobile: sm (0.9rem).
 */
export const FooterLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

/**
 * Price: cyan, bold.
 * Desktop: xl (2rem).
 * Mobile: lg (1.45rem).
 */
export const Price = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.cyan};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;
